import type { DefaultPaginatedFilters } from "@/api/shared/filters";
import { useBasicFilters, useTableFilter } from "@/composables/shared/filters/useTableFilter";
import type { TSortItem } from "@/composables/shared/filters/useUrlSort";
import {
  useFilterFields,
  useTabFilters,
  type TFilterFieldMap,
  type TFilterFields,
  type TTabSource,
} from "./useFilterFields";
import {
  computed,
  isRef,
  isReadonly,
  ref,
  toValue,
  watch,
  type MaybeRefOrGetter,
  type Ref,
  type WritableComputedRef,
} from "vue";

/** Header subset used to map column keys ↔ backend `ordering` fields. */
type TSortableHeader = { key: string; sortKey?: string };

/**
 * URL-backed table filter options. When enabled, filters are derived from the route
 * search/sort params (via `useTableFilter`) and merged with `initial`.
 */
export type TOwnedFilterUrlOptions = {
  /**
   * When true, do not sync search/sort from the URL.
   *
   * Read once at setup (`useTableFilter` takes a plain boolean), so this must not change
   * over the lifetime of the query — pass a static prop, not a toggle.
   */
  listenOff?: boolean;
  /**
   * When true, write the search term into `query` instead of `search`
   * (matches APIs that use `?query=`).
   */
  useQuery?: boolean;
  /** Optional search override (e.g. prop-driven search when URL listening is off). */
  overrideSearch?: {
    value: MaybeRefOrGetter<string | null>;
    use: boolean;
  };
  /**
   * Table headers with optional `sortKey`. When set, URL `ordering` stores the backend
   * field while `options.sortBy` keeps the column key (and request filters are remapped).
   */
  headers?: MaybeRefOrGetter<readonly TSortableHeader[]>;
};

/**
 * Query-control params, kept separate from the filter shape.
 *
 * `enabled` is not a filter: it never reaches the API and never enters the query key.
 * It lives here rather than alongside `initial` / `url` so callers never have to strip
 * control keys out of a flat blob to recover the filter fields.
 */
export type TQueryControlParams<
  TFilters extends Partial<DefaultPaginatedFilters>,
  TFields extends TFilterFieldMap = TFilterFieldMap,
> = {
  /**
   * Gate for the query. Reactive sources stay live — pass a `computed` / getter and the
   * query follows it. See `useOwnedEnabledRef` for the write-back contract that lazy
   * selects depend on.
   */
  enabled?: MaybeRefOrGetter<boolean>;
  /**
   * Declarative UI filter fields, wired against the owned filters ref.
   *
   * Each entry owns its URL params *and* the request-filter keys it maps to, and surfaces as
   * a writable model on the result's `f` bag — so a page binds `v-model="query.f.<name>"`
   * and holds no filter state of its own. See `useFilterFields`.
   *
   * Distinct from `filters`, which is the raw API filter bag (injection escape hatch).
   * Requires owned filters: pass `fields` with `initial` / `url`, not with `filters`.
   */
  fields?: TFields;
  /**
   * Active tab whose `filters` slice is merged into the request, replacing the previous
   * tab's keys on every switch.
   */
  tabs?: TTabSource<TFilters>;
};

/**
 * Filter-ownership params for list / paginated queries, as a discriminated union: the three
 * modes are mutually exclusive, so passing `filters` together with `initial` / `url` is a
 * compile error rather than a silently-dropped field.
 *
 * Filter shape is the API filter interface — callers only pass `initial` values
 * (and optional URL/table wiring). Inject `filters` only when a parent already owns them.
 */
export type TOwnedFilterParams<
  TFilters extends Partial<DefaultPaginatedFilters>,
  TFields extends TFilterFieldMap = TFilterFieldMap,
> = TQueryControlParams<TFilters, TFields> &
  (
    | {
        /** Inject parent-owned filters (escape hatch). */
        filters: MaybeRefOrGetter<Partial<TFilters>>;
        initial?: never;
        url?: never;
      }
    | {
        filters?: never;
        /**
         * Seed API filter fields for owned filters (e.g. `page_size`, static scopes).
         *
         * Prefer mutating `query.filters` for live UI filters (selects, ranges, tabs). A
         * reactive `initial` still works when something upstream owns the values, but it is
         * not required for url-mode dashboards.
         */
        initial?: MaybeRefOrGetter<Partial<TFilters>>;
        /**
         * Own URL-backed table filters (search + sort) instead of a plain mutable ref.
         * Pass `true` for defaults, or an options object.
         */
        url?: boolean | TOwnedFilterUrlOptions;
      }
  );

export type TOwnedFilterRefsResult<
  TFilters extends Partial<DefaultPaginatedFilters>,
  TFields extends TFilterFieldMap = TFilterFieldMap,
> = {
  /**
   * Writable in owned modes (`initial` / no params) **and** in `url` mode.
   *
   * In `url` mode the ref is a mutable bag: search/sort are synced from the URL, and the page
   * mutates everything else on `filters.value` (selects, date ranges, tab extras). Injected
   * computed/getter `filters` stay derived — writes are ignored, mutate the source instead.
   */
  filters: Ref<Partial<TFilters>>;
  queryEnabled: Ref<boolean>;
  /** Present when `url` mode is on — bind to the data table's `v-model:options`. */
  options?: WritableComputedRef<{ sortBy: TSortItem[] }>;
  /**
   * Writable models for the declared `fields`, keyed by declaration name. Empty when no
   * `fields` were declared.
   */
  f: TFilterFields<TFields>;
  /** Clears every declared field (models, URL params, and the filter keys they own). */
  resetFields: () => void;
};

/**
 * Creates or adopts filter + enabled refs for list/paginated queries.
 *
 * Ownership modes (first match wins):
 * 1. `filters` provided → inject / observe parent ownership
 * 2. `url` set → own URL-backed table filters via `useTableFilter(initial)`
 * 3. else → own a mutable ref via `useBasicFilters(initial)`
 */
export function useOwnedFilterRefs<
  TFilters extends Partial<DefaultPaginatedFilters>,
  TFields extends TFilterFieldMap = TFilterFieldMap,
>(params: TOwnedFilterParams<TFilters, TFields> = {}): TOwnedFilterRefsResult<TFilters, TFields> {
  let filters: Ref<Partial<TFilters>>;
  let options: WritableComputedRef<{ sortBy: TSortItem[] }> | undefined;

  if (params.filters !== undefined) {
    const injected = params.filters;
    if (isRef(injected)) {
      filters = injected as Ref<Partial<TFilters>>;
    } else {
      filters = computed(() => toValue(injected) ?? ({} as Partial<TFilters>)) as Ref<
        Partial<TFilters>
      >;
    }
  } else if (params.url) {
    const urlOpts: TOwnedFilterUrlOptions = typeof params.url === "object" ? params.url : {};
    const table = useTableFilter<TFilters & DefaultPaginatedFilters>(
      urlOpts.listenOff ?? false,
      () => (toValue(params.initial) ?? {}) as Partial<TFilters & DefaultPaginatedFilters>,
      urlOpts.useQuery ?? false,
      urlOpts.overrideSearch,
      urlOpts.headers
    );
    filters = table.filters as Ref<Partial<TFilters>>;
    options = table.options;
  } else {
    filters = useBasicFilters<TFilters & DefaultPaginatedFilters>(
      toValue(params.initial) as Partial<TFilters & DefaultPaginatedFilters> | undefined
    ) as Ref<Partial<TFilters>>;
  }

  const queryEnabled = useOwnedEnabledRef(params.enabled);

  // Tabs merge before fields hydrate, so a tab's slice never overwrites a deep-linked
  // field value that resolves in the same tick.
  if (params.tabs !== undefined) {
    useTabFilters<TFilters>(filters, params.tabs);
  }

  const { f, reset: resetFields } = params.fields
    ? useFilterFields<TFilters, TFields>(filters, params.fields)
    : { f: {} as TFilterFields<TFields>, reset: () => {} };

  return options
    ? { filters, queryEnabled, options, f, resetFields }
    : { filters, queryEnabled, f, resetFields };
}

/**
 * `queryEnabled` must satisfy two callers at once:
 * - the query itself, which needs to follow a reactive source (`computed(() => isOpen)`)
 * - `useSelectOptionItems`, which *writes* `true` to open a lazy gate
 *
 * A writable ref is adopted as-is (the owner keeps control). Anything else reactive
 * (computed / getter) is wrapped in a writable computed that *reads through* to the source,
 * so the query keeps following it for the source's whole lifetime.
 *
 * A local write (the lazy-gate open) is held in an override ref and wins only while it is
 * more permissive than the source; the next source change clears it. Mirroring into a plain
 * `ref` + `watch` instead — as this did before — silently desynchronizes: `watch` is not
 * immediate, so the mirror starts at a stale value, and any local write permanently detaches
 * the query from a source that later flips back.
 */
function useOwnedEnabledRef(enabled: MaybeRefOrGetter<boolean> | undefined): Ref<boolean> {
  if (enabled === undefined) return ref(true);

  // Writable ref → adopt, so writes propagate back to the owner.
  if (isRef(enabled) && !isReadonly(enabled)) return enabled as Ref<boolean>;

  // Plain boolean → nothing to track.
  if (typeof enabled === "boolean") return ref(enabled);

  // Readonly ref / computed / getter → read through so it stays live, with a local override
  // for the lazy gate.
  const override = ref<boolean | null>(null);
  watch(
    () => toValue(enabled),
    () => {
      override.value = null;
    }
  );
  return computed({
    get: () => override.value ?? toValue(enabled),
    set: (value: boolean) => {
      override.value = value;
    },
  }) as Ref<boolean>;
}
